import { Flex } from 'antd';
import type { FC } from 'react';

import type { AnalyticsTopic } from '@shared/api';
import { Text } from '@shared/ui/antd';
import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';
import { LoadingState } from '@shared/ui/loading-state/loading-state';

import type { TopicOption } from '../lib/dialog-summary-utils';
import { DialogSummaryFilters } from './dialog-summary-filters';
import styles from '../dialog-summary.module.scss';

type DialogSummaryQuestionsTabProps = {
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  topicsPlaceholder: string;
  noDataLabel: string;
  selectedTopics: string[];
  topicOptions: TopicOption[];
  filteredTopics: AnalyticsTopic[];
  onTopicChange: (value: unknown) => void;
};

export const DialogSummaryQuestionsTab: FC<DialogSummaryQuestionsTabProps> = ({
  isLoading,
  isError,
  refetch,
  topicsPlaceholder,
  noDataLabel,
  selectedTopics,
  topicOptions,
  filteredTopics,
  onTopicChange,
}) => (
  <>
    <DialogSummaryFilters
      placeholder={topicsPlaceholder}
      selectedTopics={selectedTopics}
      topicOptions={topicOptions}
      onTopicChange={onTopicChange}
    />

    {isLoading && <LoadingState />}
    {!isLoading && isError && <FetchingErrorState refetch={refetch} />}

    {!isLoading && !isError && (
      <>
        {!filteredTopics.length && (
          <Text variant="sm" style={{ color: 'var(--color-text-tertiary)' }}>
            {noDataLabel}
          </Text>
        )}

        {!!filteredTopics.length && (
          <Flex vertical className={styles.content}>
            <div className={styles.listScroll}>
              <Flex vertical className={styles.questionList}>
                {filteredTopics.flatMap((topic) =>
                  topic.questions.map((question) => (
                    <Flex
                      align="center"
                      justify="space-between"
                      className={styles.questionRow}
                      key={`${topic.topic}-${question.question}`}
                    >
                      <Flex align="center" gap={8} className={styles.questionMeta}>
                        <Text variant="md" className={styles.questionText} hasTooltip maxLength={60}>
                          {question.question}
                        </Text>

                        <Text
                          variant="xs"
                          fontWeight={400}
                          className={styles.questionTopic}
                          hasTooltip
                          maxLength={15}
                        >
                          {topic.topic}
                        </Text>
                      </Flex>
                      <Text variant="sm" fontWeight={400} className={styles.questionCount}>
                        {question.count}
                      </Text>
                    </Flex>
                  )),
                )}
              </Flex>
            </div>
          </Flex>
        )}
      </>
    )}
  </>
);
